import { Library } from "lucide-react";
import { CONDITION_ABBR } from "../config/variants";
import { isCustomVariant } from "../lib/variants";
import { getCollectionKey } from "../lib/collection";
import { ballForVariant } from "../lib/balls";
import { rarityInfo } from "../lib/rarity";
import { CardImage } from "./CardImage";

// Vue « classeur » : chaque variante occupe sa propre pochette, et les pochettes
// d'une même carte sont côte à côte — comme la Normale et sa Reverse dans deux
// pochettes voisines d'un vrai classeur.
//
// Deux zones, parce qu'un classeur ne se range pas pareil partout :
//  - les cartes du set officiel, chacune sur DEUX pochettes ; celles qui n'ont
//    qu'une variante (les ex, les rares sans reverse) reçoivent une pochette
//    vide au logo de l'extension, pour que l'alignement des paires tienne ;
//  - les cartes hors numérotation officielle (secrètes de fin de set), à la
//    suite, simplement les unes après les autres : les apparier n'aurait aucun
//    sens puisqu'elles n'ont pas de reverse.
export const BinderGrid = ({
  cards, collectionMap, conditionMap = {}, cardVariants, onCardClick, onVariantClick,
  officialCount, setLogo,
}) => {
  const slotsOf = (card) => {
    const variants = (cardVariants(card) || []).filter(v => v.type !== "loading");
    // Variante "loading" seule = carte pas encore enrichie : on montre quand
    // même une pochette Normale plutôt qu'une case vide.
    return variants.length ? variants : [{ key: "v-normal", type: "normal", label: "Normale" }];
  };

  // Une carte est "secrète" si son numéro dépasse le total officiel du set.
  const isSecret = (card) => {
    const n = parseInt(String(card.localId), 10);
    return officialCount ? !Number.isNaN(n) && n > officialCount : false;
  };

  const main = cards.filter(c => !isSecret(c));
  const secrets = cards.filter(isSecret);

  // Même vocabulaire que PokemonCardTile (2026-09-26/27) : ball de la
  // variante avec halo bleu = possédée / rouge = recherchée / grisée =
  // manquante — la vue Classeur affichait encore les anciennes lettres
  // (N/R/H...) et un liseré vert/bleu d'avant la refonte.
  const renderSlot = (card, variant) => {
    const key = getCollectionKey(card.id, variant.type);
    const status = collectionMap[key];
    const condition = status === "owned" ? conditionMap[key] : null;
    const isCustom = isCustomVariant(variant.type);
    const ball = ballForVariant(variant.type, isCustom ? variant.type : undefined, rarityInfo(card.rarity).tier, card.rarity);
    const state = status === "owned" ? "own" : status === "wanted" ? "want" : "none";
    return (
      <div key={variant.key} className={`binder-slot ${status ? "" : "binder-slot-ghost"} ${state !== "none" ? `binder-slot-${state}` : ""}`}>
        <div className="relative h-full cursor-pointer"
          onClick={(e) => onVariantClick(card.id, variant.type, status, e.shiftKey)}
          title={`${card.name} — ${variant.label} · clic = possédée, Maj+clic = recherchée`}>
          {card.image ? (
            <CardImage card={card} quality="low" className="w-full h-full object-cover binder-slot-img" />
          ) : (
            <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
              <Library className="w-5 h-5 text-zinc-600" />
            </div>
          )}
          <span className={`binder-ball ball-${state}`} title={`${variant.label} — ${ball.name}`}>
            <img src={ball.src} alt="" draggable="false" />
            {isCustom && <span className="ball-abbr" style={{ background: variant.custom?.color || "#52525b" }}>{variant.custom?.abbr || "?"}</span>}
          </span>
          {condition && <span className="binder-condition">{CONDITION_ABBR[condition] || condition}</span>}
        </div>
      </div>
    );
  };

  const renderCard = (card, pad) => {
    const slots = slotsOf(card);
    return (
      <div key={card.id} className="binder-card">
        <div className="binder-slots">
          {slots.map(v => renderSlot(card, v))}
          {/* Pochette vide au logo de l'extension : elle marque la place que
              cette carte n'occupe pas (pas de reverse), sans casser les paires. */}
          {pad && slots.length === 1 && (
            <div className="binder-slot binder-empty" title="Cette carte n'existe pas en Reverse">
              {setLogo && <img src={setLogo} alt="" loading="lazy" />}
            </div>
          )}
        </div>
        <button className="binder-name" onClick={() => onCardClick(card)} title={`${card.name} — ouvrir la fiche`}>
          <span className="text-zinc-500 mr-1">{card.localId}</span>{card.name}
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="binder-grid">
        {main.map(card => renderCard(card, true))}
      </div>
      {secrets.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-zinc-400 mt-8 mb-3">
            Hors numérotation officielle
            <span className="ml-2 text-xs font-normal text-zinc-600">{secrets.length} cartes</span>
          </h3>
          <div className="binder-grid binder-grid-loose">
            {secrets.map(card => renderCard(card, false))}
          </div>
        </>
      )}
    </>
  );
};
